import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { Person } from "@/components/patterns";
import { InlineCell } from "./InlineCell";

interface Rep {
  id: string;
  first_name: string;
  last_name?: string;
}
const cache = new Map<string, Rep[]>();
const nameOf = (o?: { first_name: string; last_name?: string } | null) => (o ? `${o.first_name} ${o.last_name || ""}`.trim() : "");

/** The people a record can be given to, loaded once per kind of record and kept */
function useAssignable(forLead: boolean, enabled: boolean) {
  const key = forLead ? "lead" : "all";
  const [reps, setReps] = useState<Rep[]>(cache.get(key) || []);
  useEffect(() => {
    if (!enabled || cache.has(key)) return;
    let live = true;
    api
      .get(`/api/User/Assignable${forLead ? "?for=lead" : ""}`)
      .then((r) => {
        const rows = unwrapArray<Rep>(r);
        cache.set(key, rows);
        if (live) setReps(rows);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [key, enabled, forLead]);
  return reps;
}

/**
 * The owner column, editable in place by whoever may reassign: pick a person and the record moves to them with the same
 * rules as the Reassign / Assign owner actions. `onSaved` gets the refreshed row (new owner included).
 */
export function OwnerCell({
  list,
  id,
  ownerId,
  owner,
  enabled,
  forLead = false,
  unassignedLabel,
  onSaved,
  onConflict,
}: {
  list: "Lead" | "Deal" | "Account" | "Contact" | "SalesOrder" | "Sample" | "Quotation";
  id: string;
  ownerId?: string | null;
  owner?: Rep | null;
  enabled: boolean;
  forLead?: boolean;
  unassignedLabel?: string;
  onSaved: (row: Record<string, unknown>) => void;
  onConflict?: () => void;
}) {
  const reps = useAssignable(forLead, enabled);
  const options = reps.map((r) => ({ value: r.id, label: nameOf(r) }));
  return (
    <InlineCell
      list={list}
      id={id}
      field="owner_id"
      kind="enum"
      value={ownerId ?? null}
      options={ownerId && !reps.some((r) => r.id === ownerId) ? [{ value: ownerId, label: nameOf(owner) || "Current owner" }, ...options] : options}
      enabled={enabled}
      display={<Person name={nameOf(owner) || unassignedLabel || null} />}
      onSaved={onSaved}
      onConflict={onConflict}
    />
  );
}
